/**
 * Couche interface : affiche les écrans, remonte les clics.
 * Aucune logique métier ici — tout est piloté par App.ts via des callbacks.
 */

export type ScreenName = 'landing' | 'loading' | 'ar' | 'preview' | 'error';

export interface UICallbacks {
  onStartCamera: () => void;
  onRecord: () => void;
  onStopRecording: () => void;
  onDownload: () => void;
  onShare: () => void;
  onRestart: () => void;
  onRetry: () => void;
}

export class UIController {
  private readonly screens = new Map<ScreenName, HTMLElement>();
  private toastTimeout = 0;

  constructor(callbacks: UICallbacks) {
    document.querySelectorAll<HTMLElement>('[data-screen]').forEach((el) => {
      this.screens.set(el.dataset.screen as ScreenName, el);
    });

    this.bind('btn-start', callbacks.onStartCamera);
    this.bind('btn-record', callbacks.onRecord);
    this.bind('btn-stop', callbacks.onStopRecording);
    this.bind('btn-download', callbacks.onDownload);
    this.bind('btn-share', callbacks.onShare);
    this.bind('btn-restart', callbacks.onRestart);
    this.bind('btn-retry', callbacks.onRetry);
  }

  get arContainer(): HTMLElement {
    return this.el('ar-container');
  }

  showScreen(name: ScreenName): void {
    this.screens.forEach((el, key) => el.classList.toggle('is-active', key === name));
  }

  setLoadingMessage(message: string): void {
    this.el('loading-message').textContent = message;
  }

  /** États visuels de l'écran AR. */
  setScanMode(mode: 'searching' | 'tracking' | 'recording'): void {
    this.el('scan-hint').classList.toggle('is-hidden', mode !== 'searching');
    this.el('locked-badge').hidden = mode !== 'tracking';
    this.el('btn-record').hidden = mode !== 'tracking';
    this.el('recording-hud').hidden = mode !== 'recording';
    if (mode !== 'searching') this.el('tracking-lost-hint').hidden = true;
  }

  showTrackingLostHint(visible: boolean): void {
    this.el('tracking-lost-hint').hidden = !visible;
  }

  updateTimer(totalSeconds: number): void {
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const ss = String(Math.floor(totalSeconds % 60)).padStart(2, '0');
    this.el('recording-timer').textContent = `${mm}:${ss}`;
  }

  showPreview(url: string, options: { isImage: boolean; canShare: boolean }): void {
    const video = this.el<HTMLVideoElement>('preview-video');
    const image = this.el<HTMLImageElement>('preview-image');
    video.hidden = options.isImage;
    image.hidden = !options.isImage;
    if (options.isImage) {
      image.src = url;
      video.removeAttribute('src');
    } else {
      video.src = url;
      video.load();
    }
    this.el('btn-share').hidden = !options.canShare;
    this.showScreen('preview');
  }

  clearPreview(): void {
    this.el<HTMLVideoElement>('preview-video').removeAttribute('src');
    this.el<HTMLImageElement>('preview-image').removeAttribute('src');
  }

  showError(title: string, message: string): void {
    this.el('error-title').textContent = title;
    this.el('error-message').textContent = message;
    this.showScreen('error');
  }

  showInAppBrowserNotice(appName: string): void {
    this.el('inapp-name').textContent = appName;
    this.el('inapp-notice').hidden = false;
  }

  showToast(message: string, durationMs = 4500): void {
    const toast = this.el('toast');
    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(this.toastTimeout);
    this.toastTimeout = window.setTimeout(() => { toast.hidden = true; }, durationMs);
  }

  private bind(id: string, handler: () => void): void {
    this.el(id).addEventListener('click', handler);
  }

  private el<T extends HTMLElement = HTMLElement>(id: string): T {
    const node = document.getElementById(id);
    if (!node) throw new Error(`Élément UI manquant : #${id}`);
    return node as T;
  }
}
